import React, { useRef, useState, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { Play, Pause, Mic, SmilePlus } from "lucide-react";
import { resolveMediaUrl } from "@/components/FileUpload";
import { PlayableVideo } from "@/components/PlayableVideo";
import { fetchAudioPeaks, isLocalMediaUrl } from "@/lib/audioPeaks";
import { useTrackState, toggleTrack, seekTrack, setTrackRate, setTrackDuration, setTrackCardVisible, TRACK_SPEEDS, SHARED_PLAYER_GUARD } from "@/lib/nowPlaying";
import { useGuard } from "@/lib/guards";
import { getResume, primeResume } from "@/lib/videoProgress";

const fmtDuration = (s) => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${String(ss).padStart(2, "0")}`;
};
const WAVEFORM = [8, 14, 20, 12, 26, 18, 10, 22, 16, 28, 12, 20, 9, 24, 15, 19, 11, 25, 17, 13, 21, 10, 18, 26, 14, 9, 22, 16];
// In-app voice notes are capped at 60 s (Composer). A duration MEASURED from the
// audio element above this is a headerless-WebM miscalculation (Chromium reports
// bogus values like 19:30 / 2:33 for MediaRecorder blobs) → ignore it. A server-
// provided duration is always trusted (the recorder timed it), so it bypasses this.
const VOICE_NOTE_MAX = 90;
const SPEEDS = [1, 1.5, 2];
// Vitesse choisie mémorisée pour tous les vocaux de la session (WhatsApp : on ne la re-choisit pas à chaque bulle).
let _lastRateIdx = 0;
const WAVE_BARS = 40;
const DEFAULT_PEAKS = WAVEFORM.map((h) => h / 28);
// One shared AudioContext reused across bubbles (browsers cap concurrent contexts).
let _decodeCtx = null;
const getDecodeCtx = () => {
  if (!_decodeCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) _decodeCtx = new AC();
  }
  return _decodeCtx;
};
// Decode a LOCAL (blob:/data:) audio URL into ~40 normalised RMS peaks → a REAL waveform (WhatsApp-like).
// Lot 684 : réservé aux aperçus avant envoi (quelques secondes) — les fichiers du serveur passent par
// lib/audioPeaks (forme d'onde calculée côté serveur) : plus de fichier entier décodé en mémoire sur le téléphone.
// Falls back (throws) on CORS/decoding errors so the caller keeps the fixed pattern.
// decodeAudioData calls are SERIALISED (one shared context) to avoid a concurrency
// race that made some bubbles silently fall back when many decode at once.
let _decodeChain = Promise.resolve();
const decodeSerial = (buf) => {
  const run = () => {
    const ctx = getDecodeCtx();
    if (!ctx) return Promise.reject(new Error("no-audiocontext"));
    return ctx.decodeAudioData(buf);
  };
  const p = _decodeChain.then(run, run);
  _decodeChain = p.then(() => {}, () => {});
  return p;
};
export const computePeaks = async (url) => {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  const audioBuf = await decodeSerial(buf);
  const raw = audioBuf.getChannelData(0);
  const block = Math.max(1, Math.floor(raw.length / WAVE_BARS));
  const out = [];
  let max = 0;
  for (let i = 0; i < WAVE_BARS; i += 1) {
    let sum = 0;
    for (let j = 0; j < block; j += 1) { const v = raw[i * block + j] || 0; sum += v * v; }
    const rms = Math.sqrt(sum / block);
    out.push(rms);
    if (rms > max) max = rms;
  }
  return out.map((v) => (max > 0 ? Math.max(0.12, v / max) : 0.3));
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// Build a SAME-ORIGIN proxy URL (permissive CORS) for waveform decoding so the
// real waveform works even when playback is served from the cross-origin CDN.
export const proxyPeaksUrl = (rawUrl) => {
  if (!rawUrl) return "";
  if (rawUrl.startsWith("data:") || rawUrl.startsWith("blob:")) return rawUrl;
  const idx = rawUrl.indexOf("/api/files/");
  if (idx >= 0) {
    const path = rawUrl.slice(idx + "/api/files/".length);
    if (path.startsWith("proxy/")) return `${BACKEND_URL}/api/files/${path}`;
    return `${BACKEND_URL}/api/files/proxy/${path}`;
  }
  return rawUrl.startsWith("http") ? rawUrl : `${BACKEND_URL}${rawUrl}`;
};

// WhatsApp-style voice-note player: waveform + play on top, duration (left) and
// send time + receipt (right) on the bottom row, sender avatar with a mic badge.
// Lot 685 — `track` ({ id, title, subtitle, avatar }) : publication SON → le lecteur devient la vue/télécommande du
// lecteur PARTAGÉ (lib/nowPlaying) : le son survit au défilement et au démontage de la carte (fil allégé), la barre
// flottante NowPlayingBar prend le relais hors écran. Sans `track` (vocal du chat) : lecteur local inchangé.
export const ChatAudio = ({ url, peaksUrl = null, mine = false, duration, testId = "chat-media-audio", timeEl = null, sending = false, avatarUrl = null, maxMeasured = VOICE_NOTE_MAX, onReactOpen = null, reactActive = false, track = null }) => {
  const { t } = useApp();
  const ref = useRef(null);
  const rootRef = useRef(null);
  const measuringRef = useRef(false);
  const [playingLocal, setPlayingLocal] = useState(false);
  const [curLocal, setCurLocal] = useState(0);
  const [dur, setDur] = useState(isFinite(duration) && duration > 0 ? Number(duration) : 0);
  const [rateIdxLocal, setRateIdxLocal] = useState(_lastRateIdx);
  const [peaks, setPeaks] = useState(DEFAULT_PEAKS);
  const waveRef = useRef(null);
  const scrubbing = useRef(false);
  const sharedOn = useGuard(SHARED_PLAYER_GUARD);
  const shared = !!track?.id && sharedOn;
  const np = useTrackState(shared ? track.id : null);
  const isCurrent = shared && np.track?.id === track.id;
  // Lot 686 — carte d'une publication déjà écoutée (pas en cours) : progression + « Reprendre » à la position mémorisée.
  const savedT = shared && !isCurrent && track.resume ? getResume(track.id) : 0;
  const playing = shared ? (isCurrent && np.playing) : playingLocal;
  const cur = shared ? (isCurrent ? np.cur : savedT) : curLocal;
  const shownDur = shared && isCurrent && np.dur > 0 ? np.dur : dur;
  const rateIdx = shared ? Math.max(0, TRACK_SPEEDS.indexOf(np.rate)) : rateIdxLocal;
  const rate = SPEEDS[rateIdx];
  const isLocal = isLocalMediaUrl(url);
  const sharedTrack = () => ({ ...track, url, duration: dur });
  // Position reçue avec la publication (resume_t) → mémoire locale, sans écraser une valeur plus fraîche.
  useEffect(() => { if (shared && track.resume) primeResume(track.id, Number(track.resumeAt) || 0); }, [shared, track?.id, track?.resume, track?.resumeAt]);
  // Lot 684 — « la lecture refuse parfois » : la promesse play() n'était pas gérée. Un refus (source non chargée,
  // lecture annulée par un seek) → on recharge le média et on relance une fois ; un 2e échec reste silencieux.
  const startPlayback = (a) => {
    a.playbackRate = rate;
    const p = a.play();
    if (!p?.catch) return;
    p.catch(() => {
      try { a.load(); } catch { /* noop */ }
      const again = a.play();
      if (again?.catch) again.catch(() => {});
    });
  };
  const toggle = (e) => {
    e?.stopPropagation();
    if (shared) { toggleTrack(sharedTrack()); return; }
    const a = ref.current;
    if (!a) return;
    if (a.paused) startPlayback(a); else a.pause();
  };
  const cycleRate = (e) => {
    e?.stopPropagation();
    const next = (rateIdx + 1) % SPEEDS.length;
    if (shared) { setTrackRate(TRACK_SPEEDS[next]); return; }
    setRateIdxLocal(next);
    _lastRateIdx = next;
    const a = ref.current;
    if (a) a.playbackRate = SPEEDS[next];
  };
  // Carte de la piste partagée à l'écran ou non (démontée par le fil allégé = hors écran) → barre flottante.
  useEffect(() => {
    if (!shared) return undefined;
    const id = track.id;
    const node = rootRef.current;
    let io = null;
    if (node && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(([entry]) => setTrackCardVisible(id, !!entry?.isIntersecting), { threshold: 0 });
      io.observe(node);
    }
    return () => { io?.disconnect(); setTrackCardVisible(id, false); };
  }, [shared, track?.id]);
  const applyDur = (d) => { if (isFinite(d) && d > 0) setDur(d); };
  // Element-measured duration: only accept a plausible value (guards the bogus
  // headerless-WebM number that otherwise renders as e.g. "19:30").
  const applyMeasured = (d) => { if (isFinite(d) && d > 0 && d <= maxMeasured) setDur(d); };
  const onMeta = (e) => {
    const a = e.currentTarget;
    if (isFinite(duration) && duration > 0) { applyDur(duration); return; }
    if (isFinite(a.duration) && a.duration > 0) { applyMeasured(a.duration); return; }
    // Lot 684 — fichier du serveur : la durée arrive avec la forme d'onde (lib/audioPeaks), jamais de « seek à
    // l'infini » sur iPhone (il pouvait annuler la lecture demandée).
    if (!isLocal) return;
    // Headerless WebM (MediaRecorder) reports Infinity until it is seeked to the end.
    // Seek far past the end so the browser computes the real duration, while
    // suppressing the bogus currentTime spike from updating the progress bar.
    measuringRef.current = true;
    const finish = () => {
      if (!isFinite(a.duration) || a.duration <= 0) return;
      a.removeEventListener("durationchange", finish);
      a.removeEventListener("timeupdate", finish);
      applyMeasured(a.duration);
      measuringRef.current = false;
      try { a.currentTime = 0; } catch { /* noop */ }
    };
    a.addEventListener("durationchange", finish);
    a.addEventListener("timeupdate", finish);
    try { a.currentTime = 1e101; } catch { measuringRef.current = false; }
  };
  const onTime = (e) => { if (!measuringRef.current) setCurLocal(e.currentTarget.currentTime); };
  // Real waveform (kept in state, fixed pattern as fallback). Lot 684 : fichier du serveur → forme d'onde + durée
  // calculées côté serveur (quelques octets) ; fichier local (aperçu avant envoi) → décodage sur l'appareil.
  useEffect(() => {
    const src = peaksUrl || url;
    if (!src) return undefined;
    let cancelled = false;
    const serverDuration = isFinite(duration) && duration > 0;
    (isLocalMediaUrl(src)
      ? computePeaks(src).then((p) => ({ peaks: p, duration: 0 }))
      : fetchAudioPeaks(src))
      .then((r) => {
        if (cancelled) return;
        if (Array.isArray(r?.peaks) && r.peaks.length) setPeaks(r.peaks);
        if (!serverDuration && r?.duration > 0) {
          setDur((d) => (d > 0 ? d : r.duration));
          if (track?.id) setTrackDuration(track.id, r.duration);
        }
      })
      .catch(() => { /* keep the fixed fallback pattern */ });
    return () => { cancelled = true; };
  }, [peaksUrl, url, duration, track?.id]);
  // Scrubbing: drag/tap the waveform to seek within the note.
  const seekToClientX = (clientX) => {
    const el = waveRef.current, a = ref.current;
    if (!el || !shownDur || (!shared && !a)) return;
    const rect = el.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const tt = frac * shownDur;
    if (shared) { seekTrack(sharedTrack(), tt); return; }
    try { a.currentTime = tt; } catch { /* noop */ }
    setCurLocal(tt);
  };
  const onWaveDown = (e) => { e.stopPropagation(); if (!shownDur) return; scrubbing.current = true; try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ } seekToClientX(e.clientX); };
  const onWaveMove = (e) => { if (!scrubbing.current || !shownDur) return; e.stopPropagation(); seekToClientX(e.clientX); };
  const onWaveUp = (e) => { if (scrubbing.current) { e.stopPropagation(); scrubbing.current = false; try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ } } };
  const pct = shownDur ? Math.min(1, cur / shownDur) : 0;
  const displaySecs = playing ? cur : (cur || shownDur);
  const avatarEl = avatarUrl ? (
    <div className="relative shrink-0" data-testid="chat-audio-avatar">
      <img src={avatarUrl} alt="" className="h-11 w-11 rounded-full object-cover" />
      <span
        className="absolute -bottom-0.5 -right-0.5 h-[18px] w-[18px] rounded-full grid place-items-center"
        style={{ background: mine ? "var(--yalola-sent-bubble)" : "var(--yalola-surface-alt)" }}
      >
        <Mic size={12} style={{ color: "var(--yalola-primary)" }} fill="currentColor" />
      </span>
    </div>
  ) : null;
  return (
    <div
      ref={rootRef}
      className="group rounded-2xl px-2 py-1.5 min-w-[220px] max-w-[300px]"
      style={{ background: mine ? "var(--yalola-sent-bubble)" : "var(--yalola-surface-alt)", color: "var(--yalola-text)" }}
      data-testid={testId}
      data-shared={shared ? "1" : "0"}
    >
      <div className="flex items-center gap-2">
        {mine && avatarEl}
        <button
          onClick={toggle}
          className="h-10 w-10 rounded-full grid place-items-center shrink-0 transition-transform active:scale-90"
          style={{ background: "var(--yalola-primary)", color: "#fff" }}
          data-testid="chat-audio-toggle"
          aria-label={playing ? t("chat.pause", "Pause") : t("chat.play", "Lecture")}
        >
          {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
        </button>
        <div
          ref={waveRef}
          className="relative flex-1 min-w-0 flex items-center gap-[2px] h-8 cursor-pointer select-none"
          style={{ touchAction: "pan-y" }}
          onPointerDown={onWaveDown}
          onPointerMove={onWaveMove}
          onPointerUp={onWaveUp}
          onPointerCancel={onWaveUp}
          data-testid="chat-audio-wave"
        >
          {peaks.map((p, i) => {
            const on = i / peaks.length <= pct;
            return <span key={i} className="flex-1 min-w-0 rounded-full" style={{ height: `${Math.round(4 + p * 22)}px`, background: on ? "var(--yalola-primary)" : "rgba(140,140,140,0.4)" }} />;
          })}
          <span
            className="absolute top-1/2 -translate-y-1/2 h-3 w-3 rounded-full shadow-sm pointer-events-none"
            style={{ left: `clamp(0px, calc(${pct * 100}% - 6px), calc(100% - 12px))`, background: "var(--yalola-primary)" }}
          />
        </div>
        {!mine && avatarEl}
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5 px-1 text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="tabular-nums shrink-0" data-testid="chat-audio-time">{fmtDuration(displaySecs)}</span>
          {savedT > 0 && !playing && (
            <span className="shrink-0 text-[10px] font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="chat-audio-resume">· {t("audio.resume", "Reprendre")}</span>
          )}
          {onReactOpen && !sending && (
            // Réaction rapide façon WhatsApp : 1 tap → barre d'emojis du message (la forme d'onde capte l'appui long).
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onReactOpen(); }}
              onPointerDown={(e) => e.stopPropagation()}
              onPointerUp={(e) => e.stopPropagation()}
              className={`h-5 w-5 grid place-items-center rounded-full shrink-0 transition-[opacity,transform,background-color] active:scale-90 md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 ${reactActive ? "md:opacity-100" : ""}`}
              style={{ background: reactActive ? "var(--yalola-primary)" : "transparent", color: reactActive ? "#fff" : "var(--yalola-text-muted)" }}
              title={t("chat.react", "Réagir")}
              aria-label={t("chat.react", "Réagir")}
              aria-pressed={reactActive}
              data-testid="chat-audio-react"
            >
              <SmilePlus size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={cycleRate}
            className="text-[10px] font-bold tabular-nums shrink-0 rounded-full px-1.5 h-5 grid place-items-center transition-colors active:scale-90"
            style={{
              background: rate === 1 ? "transparent" : "var(--yalola-primary)",
              color: rate === 1 ? "var(--yalola-text-muted)" : "#fff",
              border: `1px solid ${rate === 1 ? "var(--yalola-border)" : "var(--yalola-primary)"}`,
            }}
            title={t("chat.playback_speed", "Vitesse de lecture")}
            aria-label={t("chat.playback_speed", "Vitesse de lecture")}
            data-testid="chat-audio-speed"
          >{rate}×</button>
          {sending && <span className="italic leading-none" data-testid="chat-audio-sending-inline">{t("chat.sending", "Envoi…")}</span>}
          {timeEl}
        </div>
      </div>
      {!shared && (
        <audio
          ref={ref}
          src={url}
          preload="metadata"
          className="hidden"
          onPlay={() => setPlayingLocal(true)}
          onPause={() => setPlayingLocal(false)}
          onEnded={() => { setPlayingLocal(false); setCurLocal(0); }}
          onTimeUpdate={onTime}
          onLoadedMetadata={onMeta}
          onDurationChange={(e) => { if (!measuringRef.current) applyMeasured(e.currentTarget.duration); }}
        />
      )}
    </div>
  );
};

// Renders image/video media inside a chat bubble (shared with Messages page + Admin audit).
export const ChatMedia = ({ msg, className = "", onImageClick, mine = false, timeEl = null, sending = false, avatarUrl = null, onReactOpen = null, reactActive = false }) => {
  if (!msg?.media_url) return null;
  const url = resolveMediaUrl(msg.media_url);
  if (msg.media_type === "audio") {
    return <ChatAudio url={url} peaksUrl={proxyPeaksUrl(msg.media_url)} mine={mine} duration={msg.duration ?? msg.audio_duration} timeEl={timeEl} sending={sending} avatarUrl={avatarUrl} onReactOpen={onReactOpen} reactActive={reactActive} />;
  }
  if (msg.media_type === "video") {
    return <PlayableVideo src={url} className={`rounded-xl w-full ${className}`} videoClassName="w-full max-h-56 object-contain bg-black" testId="chat-media-video" />;
  }
  return (
    <img
      src={url}
      alt=""
      className={`rounded-2xl max-h-64 w-full object-cover cursor-pointer ${className}`}
      onClick={() => (onImageClick ? onImageClick(url) : window.open(url, "_blank"))}
      data-testid="chat-media-image"
    />
  );
};
